import type { FC } from "react";
import type { V2PrivacyProgress } from "../../privacy";

export const privateModeProgressText = (progress: V2PrivacyProgress) => {
  const { phase, approvalsCompleted, approvalsTotal, messageSignature } =
    progress;
  const nextApproval = approvalsCompleted + 1;
  const approval =
    approvalsTotal === undefined
      ? `${nextApproval}`
      : `${nextApproval} of ${approvalsTotal}`;
  let label: string;
  let detail: string;

  switch (phase) {
    case "wallet-signature-ready":
      label = "Continue · 2 of 2";
      detail = "Tap Continue to approve the same unlock message again.";
      break;
    case "wallet-signature":
      label = messageSignature
        ? `Signature ${messageSignature.current} of ${messageSignature.total}`
        : `Sign · ${approval}`;
      detail =
        messageSignature && messageSignature.current > 1
          ? "Approve the same message again to verify consistent recovery keys. This is not a transaction."
          : "Approve the unlock message in your wallet. This unlocks your private keys locally; it is not a transaction.";
      break;
    case "recovering":
      label = "Finding account…";
      detail =
        "Checking encrypted recovery notes. No wallet approval is needed right now.";
      break;
    case "verifying":
      label = "Checking account…";
      detail =
        "Checking the contract and proof files. No wallet approval is needed right now.";
      break;
    case "registration-proof":
      label = "Preparing proof…";
      detail =
        "Generating your proof in this browser. No wallet approval is needed.";
      break;
    case "registration-relaying":
      label = "Creating account…";
      detail = "Submitting your registration. No wallet approval is needed.";
      break;
    case "registration-simulation":
      label = "Estimating fees…";
      detail =
        "Preparing the registration transaction before asking for your approval.";
      break;
    case "registration-approval":
      label = `Register · ${approval}`;
      detail =
        "Review the registration transaction and fees in your wallet, then wait for confirmation.";
      break;
    case "registration-confirming":
      label = "Checking account…";
      detail =
        "Checking your confirmed registration on-chain. No further signature is needed.";
      break;
    case "subwallet-proof":
      label = "Preparing proof…";
      detail =
        "Generating your proof in this browser. No wallet approval is needed.";
      break;
    case "subwallet-relaying":
      label = "Creating account…";
      detail =
        "The relayer is submitting your proof and waiting for confirmation. No wallet approval is needed.";
      break;
    case "subwallet-confirming":
      label = "Checking account…";
      detail =
        "Checking your spending account on-chain before opening Private Mode.";
      break;
  }

  return { label, detail };
};

export const PrivateModeProgress: FC<{ progress: V2PrivacyProgress }> = ({
  progress,
}) => {
  const { label, detail } = privateModeProgressText(progress);
  return (
    <div
      className="private-login-modal__progress"
      role="status"
      aria-live="polite"
      aria-atomic="true">
      <span className="visually-hidden">{label}. </span>
      <p>{detail}</p>
    </div>
  );
};
